import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import * as api from '../api/client';
import { DetailDrawer } from '../components/DetailDrawer';
import { escapeHtml, fmtDate, fmtDateTime } from '../lib/cells';
import { labelForCategory } from '../data/category-labels';

interface LeadRow {
  id: number;
  customer_id: number;
  /** v_lead_list VIEW 에서 LEFT JOIN 으로 같이 받는 customer.name */
  customer_name: string | null;
  customer_business_no: string | null;
  customer_contact: string | null;
  inquiry_channel: api.InquiryChannel;
  status: api.LeadStatus;
  inquiry_summary: string | null;
  assigned_to: string | null;
  expected_close_date: string | null;
  created_at: string;
  updated_at: string;
}

interface LeadListData {
  rows: LeadRow[];
  total: number;
  loading: boolean;
  errorMessage: string;
  /** 진행중(closed/lost 제외) / 완료 / 취소 / 전체 */
  filter: 'open' | 'closed' | 'lost' | 'all';
}

const CHANNEL_LABEL: Record<api.InquiryChannel, string> = {
  homepage: '홈페이지',
  kakao: '카톡',
  email: '이메일',
  phone: '유선',
  other: '기타',
};

const STATUS_LABEL: Record<api.LeadStatus, string> = {
  inquiry: '문의',
  quoted: '가견적',
  delivered: '배송완료',
  paid: '입금완료',
  closed: '완료',
  lost: '취소',
};

const STATUS_CHIP_CLASS: Record<api.LeadStatus, string> = {
  inquiry: 'chip-primary',
  quoted: 'chip-primary',
  delivered: 'chip',
  paid: 'chip',
  closed: 'chip-success',
  lost: 'chip-warning',
};

/**
 * 🎯 LeadListView — /leads
 *
 * 영업 리드 목록 + "+ 신규 리드" 버튼.
 * 행 클릭 → /leads/:id (Step 6 에서 LeadDetailView 추가 예정 — 일단 placeholder)
 */
export class LeadListView extends SolaView<LeadListData> {
  private readonly drawer = new DetailDrawer();

  override events = {
    click: {
      '@filter': 'handleFilter',
      '@new': 'handleNew',
      // ⚠️ Sola 이벤트 위임은 first-match-wins (등록 순서) →
      //    `@edit` 가 `@row` 보다 먼저 와야 함.
      //    button 의 closest('[data-action="edit"]') = self 가 먼저 매칭되어
      //    handleEditClick 만 실행되고 row 핸들러는 검사 안 됨.
      //    순서 뒤집으면 button 클릭이 부모 tr 의 row 핸들러로 먼저 잡혀버림.
      '@edit': 'handleEditClick',
      '@row': 'handleRowClick',
    },
  };

  constructor() {
    super();
    this.data = {
      rows: [],
      total: 0,
      loading: true,
      errorMessage: '',
      filter: 'open',
    };
  }

  override getTitle(): string {
    return '리드 — HCLTech';
  }

  override onMount(): void {
    void this.loadRows();
  }

  private async loadRows(): Promise<void> {
    this.data.loading = true;
    this.setLoadingBanner(true);
    try {
      // open / closed / lost / all 은 클라이언트에서 status 필터링 (enum)
      // 백엔드 'lead' 테이블은 ADMIN_TABLES.lead.source = 'v_lead_list' VIEW 경유 —
      // customer_name 이 SELECT 결과에 자동 포함됨 (별도 customer 호출 불필요).
      const r = await api.fetchRows({
        table: 'lead',
        sort: 'id',
        order: 'desc',
        limit: 200,
      });
      const all = r.rows as unknown as LeadRow[];
      this.data.rows = this.applyFilter(all, this.data.filter);
      this.data.total = r.total;
      this.data.errorMessage = '';
    } catch (err) {
      this.data.errorMessage = `리드 목록 로드 실패: ${(err as Error).message}`;
      this.data.rows = [];
    } finally {
      this.data.loading = false;
      this.rerenderRows();
    }
  }

  private applyFilter(all: LeadRow[], filter: LeadListData['filter']): LeadRow[] {
    if (filter === 'all') return all;
    if (filter === 'open') return all.filter((r) => r.status !== 'closed' && r.status !== 'lost');
    if (filter === 'closed') return all.filter((r) => r.status === 'closed');
    if (filter === 'lost') return all.filter((r) => r.status === 'lost');
    return all;
  }

  // ===== 렌더 =====

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-lead-list">
        <header class="page-header">
          <div>
            <h1>📥 리드</h1>
            <p class="page-subtitle">고객 문의 → RFQ / 세일즈로 이어지는 영업 흐름의 시작점</p>
          </div>
          <div class="page-header-actions">
            <a class="btn-primary" data-action="new" data-link href="/leads/new">+ 신규 리드</a>
          </div>
        </header>

        <div class="filter-bar">
          <button class="filter-btn" data-action="filter" data-filter="open">진행중</button>
          <button class="filter-btn" data-action="filter" data-filter="closed">완료</button>
          <button class="filter-btn" data-action="filter" data-filter="lost">취소</button>
          <button class="filter-btn" data-action="filter" data-filter="all">전체</button>
        </div>

        <div class="banner-loading hidden">목록 불러오는 중…</div>
        <div class="banner-error hidden" data-bind="errorMessage">${escapeHtml(this.data.errorMessage)}</div>

        <section class="grid-wrap">
          <div class="grid-scroll">
            <table class="grid">
              <thead>
                <tr>
                  <th class="col-num">No.</th>
                  <th>등록일</th>
                  <th>고객사</th>
                  <th>채널</th>
                  <th>상태</th>
                  <th>담당자</th>
                  <th>문의 요약</th>
                  <th class="col-actions">수정</th>
                </tr>
              </thead>
              <tbody class="lead-tbody"></tbody>
            </table>
            <div class="grid-empty hidden">표시할 리드가 없습니다.</div>
          </div>
        </section>
        ${this.drawer}
      </div>
    `;
  }

  private rerenderRows(): void {
    this.refreshFilterButtons();
    const tbody = this._rootElement?.querySelector('.lead-tbody');
    const empty = this._rootElement?.querySelector('.grid-empty');
    if (!tbody) return;

    if (this.data.rows.length === 0) {
      tbody.innerHTML = '';
      if (empty) empty.classList.remove('hidden');
      return;
    }
    if (empty) empty.classList.add('hidden');

    const rows = this.data.rows
      .map((r) => {
        // v_lead_list 가 LEFT JOIN 으로 customer.name 을 함께 반환 — 별도 lookup 없음.
        // customer 가 삭제됐거나 NULL FK 인 케이스 fallback 으로 #id 유지.
        const custLabel = r.customer_name ?? `#${r.customer_id}`;
        const statusLabel = STATUS_LABEL[r.status] ?? r.status;
        const statusCls = STATUS_CHIP_CLASS[r.status] ?? 'chip';
        const channelLabel = CHANNEL_LABEL[r.inquiry_channel] ?? r.inquiry_channel;
        return `
          <tr data-action="row" data-id="${r.id}">
            <td class="col-num"><span class="cell-numeric">${r.id}</span></td>
            <td>${escapeHtml(fmtDateTime(r.created_at))}</td>
            <td>${escapeHtml(custLabel)}</td>
            <td><span class="chip">${escapeHtml(channelLabel)}</span></td>
            <td><span class="chip ${statusCls}">${escapeHtml(statusLabel)}</span></td>
            <td>${escapeHtml(r.assigned_to ?? '')}</td>
            <td class="lead-summary">${escapeHtml(r.inquiry_summary ?? '')}</td>
            <td class="col-actions">
              <button type="button" class="btn-row-edit"
                      data-action="edit" data-id="${r.id}"
                      title="이 리드 수정">✏ 수정</button>
            </td>
          </tr>
        `;
      })
      .join('');
    tbody.innerHTML = rows;
    void labelForCategory;  // 미사용 경고 방지 — 추후 LeadDetailView 에서 활용
  }

  private refreshFilterButtons(): void {
    const root = this._rootElement;
    if (!root) return;
    root.querySelectorAll<HTMLButtonElement>('.filter-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.filter === this.data.filter);
    });
  }

  private setLoadingBanner(visible: boolean): void {
    const root = this._rootElement;
    if (!root) return;
    root.querySelector('.banner-loading')?.classList.toggle('hidden', !visible);
    if (this.data.errorMessage) {
      root.querySelector('.banner-error')?.classList.remove('hidden');
    } else {
      root.querySelector('.banner-error')?.classList.add('hidden');
    }
  }

  // ===== 이벤트 =====

  handleFilter(_e: Event, target: HTMLElement): void {
    const f = target.dataset.filter as LeadListData['filter'] | undefined;
    if (!f) return;
    this.data.filter = f;
    void this.loadRows();
  }

  handleNew(_e: Event): void {
    // <a data-link> 가 처리
  }

  /** "✏ 수정" 버튼 클릭 — drawer 안 열리게 stopPropagation 후 라우팅. */
  handleEditClick(e: Event, target: HTMLElement): void {
    e.stopPropagation();
    e.preventDefault();
    const id = target.dataset.id;
    if (!id) return;
    history.pushState(null, '', `/leads/${id}/edit`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async handleRowClick(_e: Event, target: HTMLElement): Promise<void> {
    // 수정 버튼 클릭이면 무시 (방어) — handleEditClick 이 처리.
    if (target.closest('[data-action="edit"]')) return;
    const tr = target.closest('tr[data-id]') as HTMLElement | null;
    const id = tr?.dataset.id ?? target.dataset.id;
    if (!id) return;

    // 일단 로딩 표시 — 단순 메시지
    this.drawer.openWithBody(
      `리드 #${id}`,
      '불러오는 중…',
      '<div class="drawer-section"><span class="cell-empty">로딩 중…</span></div>',
    );

    try {
      const full = await api.fetchLeadFull(id);
      const title = full.customer
        ? `${full.customer.name} — 리드 #${full.lead.id}`
        : `리드 #${full.lead.id}`;
      const subtitle = this.buildLeadSubtitle(full.lead);
      this.drawer.openWithBody(title, subtitle, this.buildLeadBody(full));
    } catch (err) {
      this.drawer.openWithBody(
        `리드 #${id}`,
        '로드 실패',
        `<div class="drawer-section"><span class="form-error">${escapeHtml((err as Error).message)}</span></div>`,
      );
    }
  }

  // ===== Drawer body 빌더 =====

  private buildLeadSubtitle(lead: Record<string, unknown>): string {
    const channel = CHANNEL_LABEL[lead.inquiry_channel as api.InquiryChannel] ?? '';
    const status = STATUS_LABEL[lead.status as api.LeadStatus] ?? '';
    const assignee = lead.assigned_to ? ` · ${lead.assigned_to}` : '';
    const created = lead.created_at ? ` · ${fmtDateTime(lead.created_at)}` : '';
    return `${channel} · ${status}${assignee}${created}`;
  }

  private buildLeadBody(full: api.LeadFullResponse): string {
    const lead = full.lead as {
      id: number;
      customer_contact?: string | null;
      inquiry_channel: api.InquiryChannel;
      status: api.LeadStatus;
      inquiry_summary?: string | null;
      assigned_to?: string | null;
      expected_close_date?: string | null;
      notes?: string | null;
      created_by?: string | null;
      created_at?: string;
      updated_at?: string;
    };
    const sections: string[] = [];

    // 1) 고객사
    if (full.customer) {
      const c = full.customer;
      const rows: Array<[string, string]> = [
        ['이름', c.name],
      ];
      if (c.business_no) rows.push(['사업자번호', String(c.business_no)]);
      if (c.representative_name) rows.push(['대표자', String(c.representative_name)]);
      if (c.representative_phone) rows.push(['전화', String(c.representative_phone)]);
      if (c.representative_email) rows.push(['이메일', String(c.representative_email)]);
      if (c.address) rows.push(['주소', String(c.address)]);
      sections.push(this.kvSection('고객사', rows));
    }

    // 2) 리드 정보
    const leadRows: Array<[string, string]> = [];
    leadRows.push(['상태', STATUS_LABEL[lead.status] ?? lead.status]);
    leadRows.push(['채널', CHANNEL_LABEL[lead.inquiry_channel] ?? lead.inquiry_channel]);
    if (lead.assigned_to) leadRows.push(['담당자', lead.assigned_to]);
    if (lead.customer_contact) leadRows.push(['담당자 연락처', lead.customer_contact]);
    if (lead.expected_close_date) leadRows.push(['예상 마감일', fmtDate(lead.expected_close_date)]);
    if (lead.created_by) leadRows.push(['등록자', lead.created_by]);
    if (lead.created_at) leadRows.push(['등록일', fmtDateTime(lead.created_at)]);
    if (lead.updated_at) leadRows.push(['수정일', fmtDateTime(lead.updated_at)]);
    sections.push(this.kvSection('리드 정보', leadRows));

    // 3) 문의 내용
    if (lead.inquiry_summary) {
      sections.push(
        `<section class="drawer-section">
          <h3 class="drawer-section-title">문의 내용</h3>
          <pre class="drawer-prose">${escapeHtml(lead.inquiry_summary)}</pre>
        </section>`,
      );
    }

    // 4) 요청 품목
    sections.push(this.itemsSection(full.items));

    // 5) 연결된 RFQ
    sections.push(this.rfqsSection(full.rfqs));

    // 6) 메모
    if (lead.notes) {
      sections.push(
        `<section class="drawer-section">
          <h3 class="drawer-section-title">대응 메모</h3>
          <pre class="drawer-prose">${escapeHtml(lead.notes)}</pre>
        </section>`,
      );
    }

    return sections.join('');
  }

  private kvSection(title: string, rows: Array<[string, string]>): string {
    const body = rows
      .map(
        ([k, v]) => `
          <div class="kv-row">
            <div class="kv-key">${escapeHtml(k)}</div>
            <div class="kv-val">${escapeHtml(v)}</div>
          </div>`,
      )
      .join('');
    return `<section class="drawer-section">
      <h3 class="drawer-section-title">${escapeHtml(title)}</h3>
      ${body}
    </section>`;
  }

  private itemsSection(items: Record<string, unknown>[]): string {
    const title = `요청 품목 (${items.length})`;
    if (items.length === 0) {
      return `<section class="drawer-section">
        <h3 class="drawer-section-title">${title}</h3>
        <span class="cell-empty">없음</span>
      </section>`;
    }
    const rows = items
      .map((it) => {
        const cat = labelForCategory(it.category as string | null | undefined);
        const opt = it.option_part ? escapeHtml(String(it.option_part)) : '<span class="cell-empty">—</span>';
        const desc = it.item_description ? escapeHtml(String(it.item_description)) : '';
        const qty = it.quantity != null ? String(it.quantity) : '';
        const rfq = it.needs_rfq === true
          ? '<span class="chip chip-on">요청</span>'
          : '<span class="cell-empty">—</span>';
        // current_stock: 추후 inventory 연동 — 일단 0 고정
        const stock = '0';
        const notes = it.notes ? escapeHtml(String(it.notes)) : '';
        return `<tr>
          <td><span class="chip">${escapeHtml(cat || '—')}</span></td>
          <td><span class="cell-mono">${opt}</span></td>
          <td>${desc}</td>
          <td class="cell-numeric">${escapeHtml(qty)}</td>
          <td class="cell-numeric">${rfq}</td>
          <td class="cell-numeric">${stock}</td>
          <td>${notes}</td>
        </tr>`;
      })
      .join('');
    return `<section class="drawer-section">
      <h3 class="drawer-section-title">${title}</h3>
      <table class="drawer-table">
        <thead>
          <tr><th>카테고리</th><th>제품번호</th><th>설명</th><th>수량</th><th>견적요청</th><th>현재 재고</th><th>메모</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
  }

  private rfqsSection(rfqs: Record<string, unknown>[]): string {
    const title = `연결된 RFQ (${rfqs.length})`;
    if (rfqs.length === 0) {
      return `<section class="drawer-section">
        <h3 class="drawer-section-title">${title}</h3>
        <span class="cell-empty">없음</span>
      </section>`;
    }
    const rows = rfqs
      .map((r) => {
        const id = r.id != null ? String(r.id) : '';
        const date = r.rfq_date ? fmtDate(r.rfq_date as string) : '';
        const supplier = r.supplier_name ? escapeHtml(String(r.supplier_name)) : `#${r.supplier_id}`;
        const part = r.option_part ? escapeHtml(String(r.option_part)) : '';
        const qty = r.requested_qty != null ? String(r.requested_qty) : '';
        const status = r.closed_at
          ? r.winning_quote_id != null
            ? '발주완료'
            : '취소'
          : '진행중';
        return `<tr>
          <td><span class="cell-mono">#${id}</span></td>
          <td>${escapeHtml(date)}</td>
          <td>${supplier}</td>
          <td><span class="cell-mono">${part}</span></td>
          <td class="cell-numeric">${escapeHtml(qty)}</td>
          <td><span class="chip">${escapeHtml(status)}</span></td>
        </tr>`;
      })
      .join('');
    return `<section class="drawer-section">
      <h3 class="drawer-section-title">${title}</h3>
      <table class="drawer-table">
        <thead>
          <tr><th>id</th><th>날짜</th><th>거래처</th><th>부품번호</th><th>수량</th><th>상태</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
  }
}
